import { useQuery } from "@tanstack/react-query";
import {
  fetchOffPagePage,
  fetchOffPageSite,
  fetchOffPageSiteAnswers,
  fetchOffPageSites,
  fetchOmsListings,
  type OffPageSiteView,
  type OmsQuery,
  type SiteKindCategory,
} from "../api/index.js";
import { queryKeys } from "../query-keys.js";

/**
 * Off-page reads.
 *
 * Every argument that changes the answer is in the key, so a page of one site
 * can never be read back from the cache as a page of another.
 */
interface OffPageRange {
  readonly from: string;
  readonly to: string;
  readonly engine?: string;
  readonly limit: number;
  readonly offset: number;
}

export function useOffPageSites(
  workspaceId: string,
  reportId: string,
  sites: OffPageSiteView,
  range: OffPageRange &
    OmsQuery & { readonly q?: string; readonly category?: SiteKindCategory },
) {
  return useQuery({
    queryKey: queryKeys.offPageSites(workspaceId, reportId, sites, range),
    queryFn: () => fetchOffPageSites(workspaceId, reportId, { ...range, sites }),
  });
}

export function useOffPageSite(
  workspaceId: string,
  reportId: string,
  domain: string,
  range: OffPageRange,
) {
  return useQuery({
    queryKey: queryKeys.offPageSite(workspaceId, reportId, domain, range),
    queryFn: () => fetchOffPageSite(workspaceId, reportId, { ...range, domain }),
  });
}

/**
 * OMS's figures for the sites on screen. Its own query, so OMS being slow or
 * down never holds up the list itself. Nothing is asked for an empty list.
 */
export function useOmsListings(workspaceId: string, domains: readonly string[]) {
  return useQuery({
    queryKey: queryKeys.omsListings(workspaceId, domains),
    queryFn: () => fetchOmsListings(workspaceId, domains),
    enabled: domains.length > 0,
  });
}

export function useOffPagePage(
  workspaceId: string,
  reportId: string,
  page: { readonly url: string; readonly show: "missing" | "all" },
  range: OffPageRange,
) {
  return useQuery({
    queryKey: queryKeys.offPagePage(workspaceId, reportId, page, range),
    queryFn: () => fetchOffPagePage(workspaceId, reportId, { ...range, ...page }),
  });
}

/** Every answer that cited one site: the site's "Responses" tab. */
export function useOffPageSiteAnswers(
  workspaceId: string,
  reportId: string,
  site: { readonly domain: string; readonly show: "missing" | "all" },
  range: OffPageRange,
) {
  return useQuery({
    queryKey: queryKeys.offPageSiteAnswers(workspaceId, reportId, site, range),
    queryFn: () => fetchOffPageSiteAnswers(workspaceId, reportId, { ...range, ...site }),
  });
}
